import React from "react";
import { ICellRendererParams } from "ag-grid-community";
import { Box, Flex, Text, Badge } from "@radix-ui/themes";
import { File as AppFile } from "@/types/breeze/File";
import { useThumbnail } from "@/hooks/useThumbnail";

interface FileCellRendererProps extends ICellRendererParams {
  value: unknown;
}

interface FileImageProps {
  file: AppFile;
  size?: number;
}

// Individual file thumbnail component
const FileImage: React.FC<FileImageProps> = ({ file, size = 24 }) => {
  // Use the specialized thumbnail hook with complete file object
  const { blobUrl, loading, error } = useThumbnail(file);

  if (loading) {
    return (
      <Box
        style={{
          width: `${size}px`,
          height: `${size}px`,
          backgroundColor: "var(--gray-3)",
          borderRadius: "var(--radius-1)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          border: "1px solid var(--gray-6)",
          animation: "pulse 1.5s infinite",
        }}
      >
        <Text size="1" color="gray">
          ⏳
        </Text>
      </Box>
    );
  }

  if (error || !blobUrl) {
    // Graceful degradation: show document icon instead of thumbnail
    // Don't log warnings for "Insufficient file data" as this is expected
    if (error && !error.includes("Insufficient file data")) {
      console.warn("Failed to load thumbnail:", {
        fileName: file.original_file_name,
        fileId: file.id,
        isPublic: file.is_public,
        error,
      });
    }
    return (
      <Box
        style={{
          width: `${size}px`,
          height: `${size}px`,
          backgroundColor: "var(--gray-3)",
          borderRadius: "var(--radius-1)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          border: "1px solid var(--gray-6)",
        }}
      >
        <Text size="1" color="gray">
          📄
        </Text>
      </Box>
    );
  }

  return (
    <img
      src={blobUrl}
      alt={file.original_file_name}
      style={{
        width: `${size}px`,
        height: `${size}px`,
        objectFit: "cover",
        borderRadius: "var(--radius-1)",
        border: "1px solid var(--gray-6)",
        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
        display: "block",
        flexShrink: 0,
      }}
    />
  );
};

// Add pulse animation styles
const pulseStyles = `
  @keyframes pulse {
    0%, 100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }
`;

export const FileCellRenderer: React.FC<FileCellRendererProps> = ({
  value,
}) => {
  // Handle different value formats
  let files: AppFile[] = [];

  if (!value) {
    return (
      <Flex align="center" style={{ height: "100%" }}>
        <Text size="2" color="gray">
          No files
        </Text>
      </Flex>
    );
  }

  // Parse the value based on different possible formats
  try {
    if (Array.isArray(value)) {
      files = value.filter(
        (item): item is AppFile =>
          item &&
          typeof item === "object" &&
          "file_type" in item &&
          "id" in item,
      );
    } else if (
      typeof value === "object" &&
      value &&
      "file_type" in value &&
      "id" in value
    ) {
      files = [value as AppFile];
    } else if (
      typeof value === "object" &&
      value &&
      "__data" in value &&
      Array.isArray((value as { __data: unknown }).__data)
    ) {
      files = (value as { __data: unknown[] }).__data.filter(
        (item): item is AppFile =>
          Boolean(
            item &&
            typeof item === "object" &&
            item !== null &&
            "file_type" in item &&
            "id" in item,
          ),
      );
    }
  } catch (error) {
    console.warn("Error parsing file cell value:", error);
    return (
      <Flex align="center" style={{ height: "100%" }}>
        <Text size="2" color="gray">
          Invalid data
        </Text>
      </Flex>
    );
  }

  if (files.length === 0) {
    return (
      <Flex align="center" style={{ height: "100%" }}>
        <Text size="2" color="gray">
          No files
        </Text>
      </Flex>
    );
  }

  // Filter to only image files for thumbnails
  const imageFiles = files.filter(
    (file) => file.file_type && file.file_type.startsWith("image/"),
  );
  const nonImageCount = files.length - imageFiles.length;

  // Show up to 3 image thumbnails
  const displayImages = imageFiles.slice(0, 3);
  const remainingImages = imageFiles.length - displayImages.length;
  const totalRemaining = remainingImages + nonImageCount;

  return (
    <>
      {/* Inject pulse animation styles */}
      <style dangerouslySetInnerHTML={{ __html: pulseStyles }} />
      <Flex
        align="center"
        gap="1"
        style={{
          height: "100%",
          padding: "2px 0",
          overflow: "hidden",
        }}
      >
        {/* Show image thumbnails */}
        {displayImages.map((file) => (
          <FileImage key={file.id} file={file} size={28} />
        ))}

        {/* Show count badge if there are more files */}
        {totalRemaining > 0 && (
          <Badge
            variant="soft"
            color="gray"
            size="1"
            style={{
              fontSize: "10px",
              minWidth: "auto",
              padding: "1px 4px",
            }}
          >
            +{totalRemaining}
          </Badge>
        )}

        {/* If no images but there are files, show file count */}
        {displayImages.length === 0 && files.length > 0 && (
          <Flex align="center" gap="1">
            <Text size="1">📄</Text>
            <Badge
              variant="soft"
              color="blue"
              size="1"
              style={{
                fontSize: "10px",
                minWidth: "auto",
                padding: "1px 4px",
              }}
            >
              {files.length}
            </Badge>
          </Flex>
        )}
      </Flex>
    </>
  );
};

export default FileCellRenderer;
